import React, { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { useLang } from "@/context/LangContext";

const WEDDING_LOGO =
  "https://customer-assets.emergentagent.com/job_ceremonial-bliss-2/artifacts/ggk47zdd_PNG%20LOGO.png";

const Hero = () => {
  const { t } = useLang();
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const y = useTransform(scrollYProgress, [0, 1], [0, 120]);
  const opacity = useTransform(scrollYProgress, [0, 0.85], [1, 0]);

  return (
    <section
      id="hero"
      ref={ref}
      data-testid="hero-section"
      className="relative min-h-[100svh] flex items-center justify-center overflow-hidden paper-noise pt-24 pb-16"
    >
      {/* Subtle vineyard background */}
      <motion.div style={{ y }} className="absolute inset-0 -z-10">
        <div
          className="absolute inset-0 bg-cover bg-center"
          style={{
            backgroundImage:
              "url('https://images.unsplash.com/photo-1637181155600-f4487dabf8ba?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjAzOTB8MHwxfHNlYXJjaHwxfHx2aW5leWFyZCUyMGF1dHVtbnxlbnwwfHx8fDE3ODI0MzQxNTh8MA&ixlib=rb-4.1.0&q=85')",
          }}
        />
        <div className="absolute inset-0 bg-cream/85" />
        <div className="absolute inset-0 bg-gradient-to-b from-cream via-transparent to-cream" />
      </motion.div>

      <motion.div
        style={{ opacity }}
        className="relative z-10 text-center px-6 max-w-4xl flex flex-col items-center"
      >
        <motion.p
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 1, delay: 0.2 }}
          className="text-[11px] md:text-xs tracking-[0.4em] uppercase text-olive mb-8"
          data-testid="hero-label"
        >
          {t.hero.label}
        </motion.p>

        {/* Wedding monogram logo inside an elegant circular frame */}
        <motion.div
          initial={{ opacity: 0, scale: 0.92, y: 20 }}
          animate={{ opacity: 1, scale: 1, y: 0 }}
          transition={{ duration: 1.2, delay: 0.35, ease: [0.16, 1, 0.3, 1] }}
          className="relative mb-10"
        >
          <div className="absolute -inset-3 rounded-full bg-gradient-to-br from-gold/25 via-transparent to-wine/15 blur-2xl opacity-70 -z-10" />
          <div className="w-40 h-40 sm:w-48 sm:h-48 md:w-56 md:h-56 rounded-full bg-cream border-[3px] border-cream shadow-[0_15px_40px_-10px_rgba(107,45,49,0.25)] flex items-center justify-center">
            <img
              data-testid="wedding-logo"
              src={WEDDING_LOGO}
              alt="Monograma Marcel &amp; Aida"
              className="w-full h-full object-contain scale-[1.55] select-none pointer-events-none"
              loading="eager"
            />
          </div>
        </motion.div>

        <motion.h1
          initial={{ opacity: 0, scale: 0.96 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ duration: 1.3, delay: 0.6, ease: [0.16, 1, 0.3, 1] }}
          className="font-script text-wine text-shadow-soft leading-[0.85]"
          data-testid="hero-names"
        >
          <span className="block text-7xl sm:text-8xl md:text-9xl lg:text-[10rem]">Marcel</span>
          <span className="block text-4xl sm:text-5xl md:text-6xl my-2 text-gold">&amp;</span>
          <span className="block text-7xl sm:text-8xl md:text-9xl lg:text-[10rem]">Aida</span>
        </motion.h1>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 1, delay: 1.15 }}
          className="mt-10 flex flex-col items-center gap-3"
        >
          <span className="divider-gold">
            <span className="font-serif text-sm md:text-base tracking-[0.3em] uppercase text-wine">
              {t.hero.date}
            </span>
          </span>
          <p className="font-editorial italic text-base md:text-lg text-olive">
            {t.hero.subtitle}
          </p>
        </motion.div>
      </motion.div>
    </section>
  );
};

export default Hero;
